// src/pages/Customers/Customers.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { UsageLimitGuard } from '../../components/subscription/SubscriptionGuard';
import { useActivityTracker } from '../../hooks/useActivityTracker';
import useCustomerPricing from '../../hooks/useCustomerPricing';
import { turkeyCities } from '../../data/turkey-cities-districts';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

// Add fadeIn animation
const fadeInKeyframes = `
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent',
        animation: 'fadeIn 0.3s ease-in-out'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px',
        animation: 'fadeIn 0.3s ease-in-out'
    },
    headerLeft: {
        display: 'flex',
        flexDirection: 'column'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    subtitle: {
        fontSize: '16px',
        color: '#64748b',
        marginTop: '4px'
    },
    headerActions: {
        display: 'flex',
        gap: '12px'
    },
    button: {
        padding: '12px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
        color: 'white',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
        transition: 'all 0.2s ease-in-out',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none'
    },
    buttonHover: {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        animation: 'fadeIn 0.3s ease-in-out'
    },
    searchContainer: {
        marginBottom: '24px',
        animation: 'fadeIn 0.3s ease-in-out'
    },
    searchInputContainer: {
        position: 'relative',
        maxWidth: '400px'
    },
    searchIcon: {
        position: 'absolute',
        left: '16px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#94a3b8',
        width: '16px',
        height: '16px'
    },
    searchInput: {
        padding: '12px 16px 12px 44px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        width: '100%',
        background: '#f8fafc',
        outline: 'none',
        transition: 'all 0.2s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    },
    searchInputFocus: {
        borderColor: '#06C167',
        boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
        background: 'white'
    },
    tableContainer: {
        maxHeight: '60vh',
        overflowY: 'auto',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        animation: 'fadeIn 0.3s ease-in-out'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden'
    },
    th: {
        background: '#f8fafc',
        padding: '16px 20px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        position: 'sticky',
        top: 0
    },
    td: {
        padding: '16px 20px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    trHover: {
        cursor: 'pointer',
        transition: 'all 0.2s',
        position: 'relative'
    },
    trHoverState: {
        backgroundColor: '#f8fafc',
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
    },
    balancePositive: {
        color: '#059669',
        fontWeight: '600'
    },
    balanceNegative: {
        color: '#dc2626',
        fontWeight: '600'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b',
        animation: 'fadeIn 0.5s ease-in-out'
    },
    emptyIcon: {
        fontSize: '48px',
        marginBottom: '16px',
        opacity: 0.7
    },
    emptyText: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px'
    },
    emptySubtext: {
        fontSize: '14px',
        color: '#64748b',
        maxWidth: '400px',
        margin: '0 auto'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100,
        animation: 'fadeInSimple 0.2s ease-out'
    },
    modalContent: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
        border: '1px solid #e2e8f0'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
    },
    modalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '24px'
    },
    formGroup: {
        marginBottom: '20px',
        display: 'flex',
        flexDirection: 'column'
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#374151',
        fontSize: '14px'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'all 0.2s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    },
    inputFocus: {
        borderColor: '#06C167',
        boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
        backgroundColor: 'white'
    },
    select: {
        width: '100%',
        padding: '12px 16px',
        fontSize: '14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        background: 'white',
        outline: 'none',
        transition: 'all 0.2s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
    },
    selectFocus: {
        borderColor: '#06C167',
        boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)'
    }
};

function AddCustomerModal({ onClose, onSave, customerGroups, onSaveGroup }) {
    const [newCustomer, setNewCustomer] = useState({ name: '', type: 'bireysel', email: '', phone: '', address: '', city: '', district: '', taxOffice: '', taxNumber: '', currency: 'TRY', customerGroupId: '', customerGroupName: '' });
    const [districts, setDistricts] = useState([]);
    const [showNewGroupInput, setShowNewGroupInput] = useState(false);
    const [newGroupName, setNewGroupName] = useState('');

    const handleCityChange = (cityName) => {
        const selectedCity = turkeyCities.find(city => city.name === cityName);
        setDistricts(selectedCity ? selectedCity.districts : []);
        setNewCustomer(prev => ({ ...prev, city: cityName, district: '' }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'city') {
            handleCityChange(value);
        } else if (name === 'customerGroupId') {
            if (value === '__new__') {
                setShowNewGroupInput(true);
                return;
            }
            const selectedGroup = customerGroups.find(g => g.id === value);
            setNewCustomer(prev => ({ ...prev, customerGroupId: value, customerGroupName: selectedGroup?.name || '' }));
        } else {
            setNewCustomer(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleCreateGroup = async () => {
        if (!newGroupName.trim()) return;
        try {
            const groupId = await onSaveGroup(newGroupName.trim());
            setNewCustomer(prev => ({ ...prev, customerGroupId: groupId, customerGroupName: newGroupName.trim() }));
            setNewGroupName('');
            setShowNewGroupInput(false);
        } catch (error) {
            toast.error('Grup oluşturulurken hata oluştu.');
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(newCustomer);
    };

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <h2 style={{ ...styles.title, fontSize: '24px', margin: 0 }}>Yeni Müşteri Ekle</h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '20px',
                            cursor: 'pointer',
                            color: '#64748b',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#f8fafc';
                            e.target.style.borderColor = '#cbd5e1';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.borderColor = '#e2e8f0';
                        }}
                    >
                        &times;
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGroup}><label style={styles.label}>Para Birimi</label>
                        <select
                            style={styles.select}
                            name="currency"
                            value={newCustomer.currency}
                            onChange={handleChange}
                            onFocus={(e) => {
                                Object.assign(e.target.style, styles.selectFocus);
                            }}
                            onBlur={(e) => {
                                Object.keys(styles.selectFocus).forEach(prop => {
                                    e.target.style[prop] = styles.select[prop];
                                });
                            }}
                        >
                            <option value="TRY">Türk Lirası (₺)</option>
                            <option value="USD">Amerikan Doları ($)</option>
                        </select>
                    </div>
                    <div style={styles.formGroup}><label style={styles.label}>Müşteri Tipi</label>
                        <select
                            style={styles.select}
                            name="type"
                            value={newCustomer.type}
                            onChange={handleChange}
                            onFocus={(e) => {
                                Object.assign(e.target.style, styles.selectFocus);
                            }}
                            onBlur={(e) => {
                                Object.keys(styles.selectFocus).forEach(prop => {
                                    e.target.style[prop] = styles.select[prop];
                                });
                            }}
                        >
                            <option value="bireysel">Bireysel</option>
                            <option value="kurumsal">Kurumsal</option>
                        </select>
                    </div>
                    <div style={styles.formGroup}><label style={styles.label}>Müşteri Grubu</label>
                        {!showNewGroupInput ? (
                            <select
                                style={styles.select}
                                name="customerGroupId"
                                value={newCustomer.customerGroupId}
                                onChange={handleChange}
                                onFocus={(e) => {
                                    Object.assign(e.target.style, styles.selectFocus);
                                }}
                                onBlur={(e) => {
                                    Object.keys(styles.selectFocus).forEach(prop => {
                                        e.target.style[prop] = styles.select[prop];
                                    });
                                }}
                            >
                                <option value="">Grup Seçin (Opsiyonel)</option>
                                {customerGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                                <option value="__new__">+ Yeni Grup Oluştur</option>
                            </select>
                        ) : (
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <input
                                    style={{ ...styles.input, flex: 1 }}
                                    type="text"
                                    placeholder="Yeni grup adı..."
                                    value={newGroupName}
                                    onChange={(e) => setNewGroupName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateGroup(); } }}
                                    autoFocus
                                />
                                <button type="button" onClick={handleCreateGroup} style={{ ...styles.button, padding: '8px 16px', fontSize: '13px', background: 'linear-gradient(135deg, #059669, #047857)' }}>Kaydet</button>
                                <button type="button" onClick={() => setShowNewGroupInput(false)} style={{ ...styles.button, padding: '8px 16px', fontSize: '13px', background: '#64748b' }}>İptal</button>
                            </div>
                        )}
                    </div>
                    <div style={styles.formGroup}><label style={styles.label}>Ad Soyad / Unvan</label>
                        <input
                            style={styles.input}
                            type="text"
                            name="name"
                            value={newCustomer.name}
                            onChange={handleChange}
                            required
                            onFocus={(e) => {
                                Object.assign(e.target.style, styles.inputFocus);
                            }}
                            onBlur={(e) => {
                                Object.keys(styles.inputFocus).forEach(prop => {
                                    e.target.style[prop] = styles.input[prop];
                                });
                            }}
                        />
                    </div>
                    <div style={styles.formGroup}><label style={styles.label}>E-posta</label>
                        <input
                            style={styles.input}
                            type="email"
                            name="email"
                            value={newCustomer.email}
                            onChange={handleChange}
                            onFocus={(e) => {
                                Object.assign(e.target.style, styles.inputFocus);
                            }}
                            onBlur={(e) => {
                                Object.keys(styles.inputFocus).forEach(prop => {
                                    e.target.style[prop] = styles.input[prop];
                                });
                            }}
                        />
                    </div>
                    <div style={styles.formGroup}><label style={styles.label}>Telefon</label>
                        <input
                            style={styles.input}
                            type="tel"
                            name="phone"
                            value={newCustomer.phone}
                            onChange={handleChange}
                            onFocus={(e) => {
                                Object.assign(e.target.style, styles.inputFocus);
                            }}
                            onBlur={(e) => {
                                Object.keys(styles.inputFocus).forEach(prop => {
                                    e.target.style[prop] = styles.input[prop];
                                });
                            }}
                        />
                    </div>
                    <div style={styles.formGroup}><label style={styles.label}>İl</label>
                        <select
                            style={styles.select}
                            name="city"
                            value={newCustomer.city}
                            onChange={handleChange}
                            onFocus={(e) => {
                                Object.assign(e.target.style, styles.selectFocus);
                            }}
                            onBlur={(e) => {
                                Object.keys(styles.selectFocus).forEach(prop => {
                                    e.target.style[prop] = styles.select[prop];
                                });
                            }}
                        >
                            <option value="">İl Seçin</option>
                            {turkeyCities.map(city => <option key={city.name} value={city.name}>{city.name}</option>)}
                        </select>
                    </div>
                    <div style={styles.formGroup}><label style={styles.label}>İlçe</label>
                        <select
                            style={styles.select}
                            name="district"
                            value={newCustomer.district}
                            onChange={handleChange}
                            disabled={!newCustomer.city}
                            onFocus={(e) => {
                                Object.assign(e.target.style, styles.selectFocus);
                            }}
                            onBlur={(e) => {
                                Object.keys(styles.selectFocus).forEach(prop => {
                                    e.target.style[prop] = styles.select[prop];
                                });
                            }}
                        >
                            <option value="">İlçe Seçin</option>
                            {districts.map(district => <option key={district} value={district}>{district}</option>)}
                        </select>
                    </div>
                    <div style={styles.formGroup}><label style={styles.label}>Adres</label>
                        <input
                            style={styles.input}
                            type="text"
                            name="address"
                            value={newCustomer.address}
                            onChange={handleChange}
                            onFocus={(e) => {
                                Object.assign(e.target.style, styles.inputFocus);
                            }}
                            onBlur={(e) => {
                                Object.keys(styles.inputFocus).forEach(prop => {
                                    e.target.style[prop] = styles.input[prop];
                                });
                            }}
                        />
                    </div>
                    {newCustomer.type === 'kurumsal' && (
                        <>
                            <div style={styles.formGroup}><label style={styles.label}>Vergi Dairesi</label>
                                <input
                                    style={styles.input}
                                    type="text"
                                    name="taxOffice"
                                    value={newCustomer.taxOffice}
                                    onChange={handleChange}
                                    onFocus={(e) => {
                                        Object.assign(e.target.style, styles.inputFocus);
                                    }}
                                    onBlur={(e) => {
                                        Object.keys(styles.inputFocus).forEach(prop => {
                                            e.target.style[prop] = styles.input[prop];
                                        });
                                    }}
                                />
                            </div>
                            <div style={styles.formGroup}><label style={styles.label}>Vergi Numarası</label>
                                <input
                                    style={styles.input}
                                    type="text"
                                    name="taxNumber"
                                    value={newCustomer.taxNumber}
                                    onChange={handleChange}
                                    onFocus={(e) => {
                                        Object.assign(e.target.style, styles.inputFocus);
                                    }}
                                    onBlur={(e) => {
                                        Object.keys(styles.inputFocus).forEach(prop => {
                                            e.target.style[prop] = styles.input[prop];
                                        });
                                    }}
                                />
                            </div>
                        </>
                    )}
                    <div style={styles.modalActions}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{ ...styles.button, background: 'linear-gradient(135deg, #64748b, #475569)', boxShadow: '0 4px 12px rgba(100, 116, 139, 0.3)' }}
                            onMouseEnter={(e) => {
                                Object.assign(e.target.style, styles.buttonHover);
                            }}
                            onMouseLeave={(e) => {
                                Object.keys(styles.buttonHover).forEach(prop => {
                                    e.target.style[prop] = styles.button[prop];
                                });
                                e.target.style.background = 'linear-gradient(135deg, #64748b, #475569)';
                                e.target.style.boxShadow = '0 4px 12px rgba(100, 116, 139, 0.3)';
                            }}
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            style={{ ...styles.button, background: 'linear-gradient(135deg, #059669, #047857)', boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)' }}
                            onMouseEnter={(e) => {
                                Object.assign(e.target.style, styles.buttonHover);
                            }}
                            onMouseLeave={(e) => {
                                Object.keys(styles.buttonHover).forEach(prop => {
                                    e.target.style[prop] = styles.button[prop];
                                });
                                e.target.style.background = 'linear-gradient(135deg, #059669, #047857)';
                                e.target.style.boxShadow = '0 4px 12px rgba(5, 150, 105, 0.3)';
                            }}
                        >
                            Kaydet
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}


function Customers() {
    const { userData, canViewPrices } = useAuth();
    const { checkUsageLimit } = useSubscription();
    const activityTracker = useActivityTracker();
    const { customerGroups, saveCustomerGroup } = useCustomerPricing(userData?.companyId);
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);

    // Inject keyframes for animations
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            @keyframes fadeIn {
                from { opacity: 0; transform: translateY(10px); }
                to { opacity: 1; transform: translateY(0); }
            }
            
            @keyframes fadeInSimple {
                from { opacity: 0; }
                to { opacity: 1; }
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        if (!userData?.companyId) {
            return;
        }
        const companyId = userData.companyId;
        const customersQuery = query(collection(db, 'companies', companyId, 'customers'));
        const unsubscribe = onSnapshot(customersQuery, (snapshot) => {
            setCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, [userData]);

    const handleAddCustomer = async (newCustomerData) => {
        if (!userData?.companyId || !newCustomerData.name) return;

        // Check for duplicate customer name
        const existingCustomer = customers.find(c => c.name.toLowerCase() === newCustomerData.name.trim().toLowerCase());
        if (existingCustomer) {
            alert('Bu isimde bir müşteri zaten mevcut! Lütfen farklı bir isim kullanın.');
            return;
        }

        // Check customer limit before adding
        const customerUsage = checkUsageLimit('customers');
        if (!customerUsage.allowed) {
            alert(`Müşteri limitiniz doldu! Mevcut plan: ${customerUsage.current}/${customerUsage.limit} müşteri. Aboneliğinizi yükseltmek için Abonelik sayfasını ziyaret edin.`);
            return;
        }

        try {
            const docRef = await addDoc(collection(db, 'companies', userData.companyId, 'customers'), { ...newCustomerData, balance: 0, createdAt: new Date() });

            // Track customer creation activity
            if (activityTracker.isTracking) {
                activityTracker.trackEntityCreate('customer', docRef.id, newCustomerData.name, {
                    customerType: newCustomerData.type,
                    currency: newCustomerData.currency,
                    city: newCustomerData.city
                });
            }

            setShowAddModal(false);
        } catch (error) {
            console.error("Müşteri eklenirken hata:", error);

            // Track error
            if (activityTracker.isTracking) {
                activityTracker.trackError('customer_creation_error', error.message, 'customers_page');
            }
        }
    };

    // Function to export customer balances to Excel
    const handleExportCustomerBalances = () => {
        // Prepare data for export
        const exportData = customers.map(customer => ({
            'Müşteri Adı': customer.name,
            'E-posta': customer.email || '-',
            'Telefon': customer.phone || '-',
            'İl': customer.city || '-',
            'İlçe': customer.district || '-',
            'Adres': customer.address || '-',
            'Müşteri Tipi': customer.type === 'kurumsal' ? 'Kurumsal' : 'Bireysel',
            'Vergi Dairesi': customer.taxOffice || '-',
            'Vergi Numarası': customer.taxNumber || '-',
            'Para Birimi': customer.currency === 'USD' ? 'USD ($)' : 'TRY (₺)',
            'Bakiye': canViewPrices ? (customer.balance || 0).toFixed(2) : '***',
            'Bakiye Değeri': canViewPrices ? `${(customer.balance || 0).toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}` : '***'
        }));

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(exportData);

        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Müşteri Bakiyeleri');

        // Generate filename with current date
        const currentDate = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
        const filename = `Musteri_Bakiyeleri_${currentDate}.xlsx`;

        // Export to Excel
        XLSX.writeFile(workbook, filename);

        // Track export activity
        if (activityTracker.isTracking) {
            activityTracker.trackExport('customer_balances', customers.length);
        }
    };

    const filteredCustomers = customers.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const customerUsage = checkUsageLimit('customers');

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <h1 style={styles.title}>Müşteriler</h1>
                    <p style={styles.subtitle}>
                        Müşteri bilgilerini yönetin ve takip edin
                        {customerUsage.limit !== -1 && (
                            <span style={{
                                marginLeft: '8px',
                                color: customerUsage.allowed ? '#059669' : '#dc2626',
                                fontWeight: '600'
                            }}>
                                ({customerUsage.current}/{customerUsage.limit} müşteri)
                            </span>
                        )}
                    </p>
                </div>
                <div style={styles.headerActions}>
                    {/* Add Export Button */}
                    <button
                        onClick={handleExportCustomerBalances}
                        style={{ ...styles.button, background: 'linear-gradient(135deg, #10b981, #059669)' }}
                        onMouseEnter={(e) => {
                            Object.assign(e.target.style, styles.buttonHover);
                        }}
                        onMouseLeave={(e) => {
                            Object.keys(styles.buttonHover).forEach(prop => {
                                e.target.style[prop] = styles.button[prop];
                            });
                            e.target.style.background = 'linear-gradient(135deg, #10b981, #059669)';
                        }}
                    >
                        📄 Excel'e Aktar
                    </button>
                    <UsageLimitGuard
                        type="customers"
                        fallback={
                            <button
                                style={{ ...styles.button, opacity: 0.6, cursor: 'not-allowed' }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const { current, limit } = checkUsageLimit('customers');
                                    toast.error(`Müşteri limitiniz doldu! Mevcut plan: ${current}/${limit} müşteri. Aboneliğinizi yükseltmek için Abonelik sayfasını ziyaret edin.`, { duration: 5000 });
                                }}
                            >
                                + Müşteri Ekle (Limit Doldu)
                            </button>
                        }
                    >
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowAddModal(true);
                            }}
                            style={styles.button}
                            onMouseEnter={(e) => {
                                Object.assign(e.target.style, styles.buttonHover);
                            }}
                            onMouseLeave={(e) => {
                                Object.keys(styles.buttonHover).forEach(prop => {
                                    e.target.style[prop] = styles.button[prop];
                                });
                            }}
                        >
                            + Müşteri Ekle
                        </button>
                    </UsageLimitGuard>
                </div>
            </div>

            <div style={styles.box}>
                <div style={styles.searchContainer}>
                    <div style={styles.searchInputContainer}>
                        <svg style={styles.searchIcon} fill="currentColor" viewBox="0 0 24 24">
                            <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 0 0 1.48-5.34c-.47-2.78-2.79-5-5.59-5.34a6.505 6.505 0 0 0-7.27 7.27c.34 2.8 2.56 5.12 5.34 5.59a6.5 6.5 0 0 0 5.34-1.48l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                        </svg>
                        <input
                            style={styles.searchInput}
                            type="text"
                            placeholder="Müşteri ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={(e) => Object.assign(e.target.style, styles.searchInputFocus)}
                            onBlur={(e) => {
                                Object.keys(styles.searchInputFocus).forEach(prop => {
                                    e.target.style[prop] = styles.searchInput[prop] || '';
                                });
                            }}
                        />
                    </div>
                </div>

                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Müşteri</th>
                                <th style={styles.th}>İletişim</th>
                                <th style={styles.th}>Grup</th>
                                <th style={styles.th}>Konum</th>
                                <th style={styles.th}>Bakiye</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCustomers.map((customer) => (
                                <tr
                                    key={customer.id}
                                    style={styles.trHover}
                                    onClick={() => navigate(`/musteriler/${customer.id}`)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = styles.trHoverState.backgroundColor;
                                        e.currentTarget.style.transform = styles.trHoverState.transform;
                                        e.currentTarget.style.boxShadow = styles.trHoverState.boxShadow;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.transform = 'none';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}
                                >
                                    <td style={styles.td}>
                                        <div style={{ fontWeight: '600', color: '#1e293b' }}>{customer.name}</div>
                                        <div style={{ fontSize: '12px', color: '#64748b' }}>{customer.type === 'kurumsal' ? 'Kurumsal' : 'Bireysel'}</div>
                                    </td>
                                    <td style={styles.td}>
                                        <div style={{ fontSize: '13px' }}>{customer.phone || '-'}</div>
                                        <div style={{ fontSize: '12px', color: '#64748b' }}>{customer.email || '-'}</div>
                                    </td>
                                    <td style={styles.td}>
                                        {customer.customerGroupName ? (
                                            <span style={{ 
                                                padding: '4px 10px', 
                                                borderRadius: '20px', 
                                                fontSize: '12px', 
                                                background: '#e0e7ff', 
                                                color: '#4338ca',
                                                fontWeight: '600'
                                            }}>
                                                {customer.customerGroupName}
                                            </span>
                                        ) : '-'}
                                    </td>
                                    <td style={styles.td}>
                                        <div style={{ fontSize: '13px' }}>{customer.city || '-'}</div>
                                        <div style={{ fontSize: '12px', color: '#64748b' }}>{customer.district || '-'}</div>
                                    </td>
                                    <td style={{ ...styles.td, ...(customer.balance < 0 ? styles.balanceNegative : styles.balancePositive) }}>
                                        {canViewPrices ? `${(customer.balance || 0).toFixed(2)} ${customer.currency === 'USD' ? '$' : '₺'}` : '***'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {filteredCustomers.length === 0 && (
                        <div style={styles.emptyState}>
                            <div style={styles.emptyIcon}>👥</div>
                            <div style={styles.emptyText}>Müşteri Bulunamadı</div>
                            <div style={styles.emptySubtext}>Aradığınız kriterlere uygun müşteri kaydı bulunmamaktadır.</div>
                        </div>
                    )}
                </div>
            </div>

             {showAddModal && <AddCustomerModal onClose={() => setShowAddModal(false)} onSave={handleAddCustomer} customerGroups={customerGroups} onSaveGroup={saveCustomerGroup} />}

        </div>
    );
}

export default Customers;
